// Dashboard page - overview and link management

import React, { useState, useEffect, useCallback, useRef } from "react";
import { FiPlus, FiSearch, FiFolder, FiUpload, FiDownload } from "react-icons/fi";
import { Link } from "react-router-dom";
import { api } from "../utils/api";
import StatCards from "../components/StatCards";
import LinkList from "../components/LinkList";
import CreateLinkModal from "../components/CreateLinkModal";
import BulkLinkImportModal from "../components/BulkLinkImportModal";
import { useToast } from "../components/ToastProvider";
import "../styles/Dashboard.css";

export default function DashboardPage() {
  const { addToast } = useToast();
  useEffect(() => {
    document.title = "Dashboard - LinkSquire";
  }, []);
  const [links, setLinks] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [linksLoading, setLinksLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const initialLoadDone = useRef(false);

  const limit = 25;

  const fetchLinks = useCallback(async () => {
    try {
      const data = await api.getLinks(page, limit, search, statusFilter);
      setLinks(data.links);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to fetch links:", err);
    }
  }, [page, search, statusFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await api.getDashboardSummary();
      setSummary(data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  }, []);

  // Initial load only — shows spinner once
  useEffect(() => {
    async function init() {
      setLoading(true);
      await Promise.all([fetchLinks(), fetchSummary()]);
      setLoading(false);
      initialLoadDone.current = true;
    }
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subsequent search / filter / page changes — show inline loading
  useEffect(() => {
    if (initialLoadDone.current) {
      setLinksLoading(true);
      fetchLinks().finally(() => setLinksLoading(false));
    }
  }, [fetchLinks]);

  const handleCreated = (newLink) => {
    // Prepend the new link to the list immediately
    setLinks((prev) => [{
      id: newLink.id,
      short_code: newLink.short_code,
      short_url: newLink.short_url,
      original_url: newLink.original_url,
      title: newLink.title || "",
      click_count: 0,
      is_active: true,
      created_at: newLink.created_at,
      expires_at: newLink.expires_at || null,
      has_password: newLink.has_password || false,
      tags: newLink.tags || "",
      mobile_url: newLink.mobile_url || "",
      redirect_type: newLink.redirect_type || "302",
      redirect_time: newLink.redirect_time != null ? newLink.redirect_time : 10,
    }, ...prev]);
    setTotal((t) => t + 1);
    setShowCreate(false);
    // Background refresh summary stats (total_links changed)
    fetchSummary();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  // Callback for when a link count changes (delete/bulk delete) — refresh summary in background
  const refreshSummary = useCallback(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Silent re-fetch of just the links list (e.g. after bulk actions)
  const refreshLinks = useCallback(() => {
    fetchLinks();
  }, [fetchLinks]);

  const totalPages = Math.ceil(total / limit);

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await api.exportLinks({ search, status: statusFilter });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `linksquire-links-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addToast("Links exported as CSV", "success");
    } catch (err) {
      addToast(err.message || "Failed to export links", "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div style={{ display: "flex", gap: "12px" }}>
          <button className="btn-secondary" onClick={() => setShowImport(true)}>
            <FiUpload style={{ marginRight: 6 }} /> Import CSV
          </button>
          <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
            <FiDownload style={{ marginRight: 6 }} /> {exporting ? "Exporting..." : "Export CSV"}
          </button>
          <Link to="/campaigns" className="btn-secondary" style={{ textDecoration: "none" }}>
            <FiFolder style={{ marginRight: 6 }} /> Campaigns
          </Link>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <FiPlus /> New Link
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading dashboard...</p>
        </div>
      ) : (
        <>
          <StatCards summary={summary} />

          <div className="links-section">
            <div className="links-header">
              <h2>Your Links {summary.total_links !== undefined && <span className="link-limit-hint">{summary.total_links}/50</span>}</h2>
              <form onSubmit={handleSearch} className="search-form">
                <div className="search-input-wrapper">
                  <FiSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search by title..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </form>
            </div>

            <LinkList
              links={links}
              setLinks={setLinks}
              setTotal={setTotal}
              refreshSummary={refreshSummary}
              refreshLinks={refreshLinks}
              statusFilter={statusFilter}
              onStatusFilter={handleStatusFilter}
              linksLoading={linksLoading}
            />

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {showCreate && (
        <CreateLinkModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {showImport && (
        <BulkLinkImportModal
          onClose={() => setShowImport(false)}
          onImported={() => {
            refreshLinks();
            refreshSummary();
          }}
        />
      )}
    </div>
  );
}
